import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ProjectsTopSection from 'components/modules/project-management/ProjectsTopSection';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pagination, Row } from 'react-bootstrap';
import { Box, CircularProgress } from '@mui/material';
import ProjectCard from 'pages/project-management/project-list/ProjectCard';
import { Project } from '@app/shared/types/project.type';
import { listATACProjects, mapATACProjectToProjectCard } from 'data-access/atac.api';

const PAGE_SIZE = 12;

const AllProjects = () => {
  const userId = localStorage.getItem('userId') || '';
  const [projectList, setProjectList] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const load = async () => {
      if (!userId) {
        setProjectList([]);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const response = await listATACProjects(userId);
        const projects = response.data?.projects ?? [];
        setProjectList(projects.map(mapATACProjectToProjectCard));
      } catch (err) {
        console.error('Failed to fetch ATE+ projects:', err);
        setProjectList([]);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [userId]);

  const { paginatedProjects, totalPages } = useMemo(() => {
    const list = projectList ?? [];
    const total = Math.ceil(list.length / PAGE_SIZE) || 1;
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return {
      paginatedProjects: list.slice(start, end),
      totalPages: total
    };
  }, [projectList, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) setCurrentPage(1);
  }, [currentPage, totalPages]);

  return (
    <div>
      <div className="d-flex flex-wrap mb-4 gap-3 gap-sm-6 align-items-center">
        <h2 className="mb-0">
          <span className="me-3">Projects</span>
          <span className="fw-normal text-body-tertiary">({isLoading ? '...' : projectList?.length || 0})</span>
        </h2>
        {userId && (
          <Link className="btn btn-primary px-5" to="/ate+/create-project">
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add new project
          </Link>
        )}
      </div>

      <ProjectsTopSection activeView="card" />

      {isLoading ? (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height={200} gap={2}>
          <CircularProgress size={50} />
          <p className="text-muted">Loading projects...</p>
        </Box>
      ) : projectList && projectList.length > 0 ? (
        <>
          <Row>
            {paginatedProjects.map(project => (
              <ProjectCard key={project.projectId} project={project} basePath="/ate+" />
            ))}
          </Row>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <Pagination className="mb-0">
                <Pagination.First
                  onClick={e => {
                    e.preventDefault();
                    setCurrentPage(1);
                  }}
                  disabled={currentPage <= 1}
                  href="#"
                />
                <Pagination.Prev
                  onClick={e => {
                    e.preventDefault();
                    setCurrentPage(p => Math.max(1, p - 1));
                  }}
                  disabled={currentPage <= 1}
                  href="#"
                />
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => setCurrentPage(page)}
                    style={{ cursor: 'pointer' }}
                  >
                    {page}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  onClick={e => {
                    e.preventDefault();
                    setCurrentPage(p => Math.min(totalPages, p + 1));
                  }}
                  disabled={currentPage >= totalPages}
                  href="#"
                />
                <Pagination.Last
                  onClick={e => {
                    e.preventDefault();
                    setCurrentPage(totalPages);
                  }}
                  disabled={currentPage >= totalPages}
                  href="#"
                />
              </Pagination>
            </div>
          )}
        </>
      ) : (
        <p className="text-center text-muted py-5">No projects found.</p>
      )}
    </div>
  );
};

export default AllProjects;
